import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { 
  ArrowLeft,
  Home,
  Wrench,
  Zap,
  FileText,
  Shield,
  DollarSign,
  ExternalLink,
  Phone,
  Users,
  Leaf,
  ClipboardList
} from "lucide-react";
import { Link } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";

const HomeownersResources = () => {
  const guidesResources = [
    {
      title: "HUD First-Time Homebuyer Guide",
      description: "Official U.S. Department of Housing guide covering everything from preparing to buy to closing the deal",
      type: "External Resource",
      icon: <Home className="w-6 h-6" />,
      color: "#3F5D4A",
      action: "Read Guide",
      link: "https://www.hud.gov/topics/buying_a_home"
    },
    {
      title: "Pennsylvania Housing Finance Agency (PHFA)",
      description: "First-time homebuyer programs, down payment assistance, and mortgage options for PA residents",
      type: "External Resource",
      icon: <DollarSign className="w-6 h-6" />,
      color: "#87A96B",
      action: "Explore Programs",
      link: "https://www.phfa.org/programs/homebuyers.aspx"
    },
    {
      title: "Philadelphia Home Repair Programs",
      description: "City of Philadelphia programs offering grants and low-interest loans for home repairs",
      type: "External Resource",
      icon: <Wrench className="w-6 h-6" />,
      color: "#3F5D4A",
      action: "View Programs",
      link: "https://www.phila.gov/services/property-lots-housing/get-help-with-home-repairs/"
    },
    {
      title: "Homeowner's Insurance Guide",
      description: "Consumer Financial Protection Bureau guide to understanding and choosing homeowner's insurance",
      type: "External Resource",
      icon: <Shield className="w-6 h-6" />,
      color: "#87A96B",
      action: "Learn More",
      link: "https://www.consumerfinance.gov/housing/housing-insecurity/help-for-homeowners/homeowners-insurance/"
    }
  ];

  const maintenanceResources = [
    {
      title: "Seasonal Home Maintenance Checklist",
      description: "HUD's comprehensive guide to maintaining your home throughout the year",
      type: "External Resource",
      icon: <ClipboardList className="w-6 h-6" />,
      color: "#3F5D4A",
      action: "View Checklist",
      link: "https://www.hud.gov/program_offices/housing/sfh/buying/homemaintenance"
    },
    {
      title: "Energy Saving Tips for Homeowners",
      description: "Department of Energy tips to reduce utility bills and improve home efficiency",
      type: "External Resource",
      icon: <Zap className="w-6 h-6" />,
      color: "#87A96B",
      action: "Save Energy",
      link: "https://www.energy.gov/energysaver/energy-saver"
    },
    {
      title: "PECO Energy Efficiency Programs",
      description: "Rebates, discounts, and programs to help Philadelphia homeowners save on energy costs",
      type: "External Resource",
      icon: <Leaf className="w-6 h-6" />,
      color: "#3F5D4A",
      action: "Get Rebates",
      link: "https://www.peco.com/ways-to-save"
    },
    {
      title: "PGW Home Weatherization",
      description: "Weatherization services for income-eligible homeowners — no cost",
      type: "External Resource",
      icon: <Home className="w-6 h-6" />,
      color: "#87A96B",
      action: "Apply Now",
      link: "https://www.pgworks.com/residential/save-money-energy/conservation-programs"
    }
  ];

  const supportResources = [
    {
      title: "Philadelphia Homeownership Counseling",
      description: "HUD-approved counseling services for homeowners facing challenges — no cost",
      type: "Counseling Service",
      icon: <Users className="w-6 h-6" />,
      color: "#3F5D4A",
      phone: "215-334-1200",
      link: "https://clarifi.org/housing-counseling/"
    },
    {
      title: "PA Property Tax/Rent Rebate Program",
      description: "Rebates for eligible seniors, widows/widowers, and people with disabilities",
      type: "Government Program",
      icon: <DollarSign className="w-6 h-6" />,
      color: "#87A96B",
      link: "https://www.revenue.pa.gov/IncentivesCreditsPrograms/PropertyTaxRentRebateProgram/Pages/default.aspx"
    },
    {
      title: "Philadelphia Mortgage Foreclosure Prevention",
      description: "Legal help and resources for homeowners at risk of foreclosure — no cost",
      type: "Legal Assistance",
      icon: <Shield className="w-6 h-6" />,
      color: "#3F5D4A",
      phone: "215-523-9500",
      link: "https://www.phillyvip.org/"
    }
  ];

  return (
    <div className="min-h-screen bg-[#F8F4ED]">
      <Header />
      <div className="py-20 px-4">
        <div className="container mx-auto max-w-7xl">
          
          {/* Back Navigation */}
          <div className="mb-8">
            <Link to="/resources" className="inline-flex items-center text-gray-600 hover:text-[#3F5D4A] transition-colors duration-200">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Resource Center
            </Link>
          </div>

          {/* Page Title */}
          <div className="text-center space-y-6 mb-12">
            <div 
              className="inline-flex items-center px-4 py-2 rounded-full text-white text-sm font-semibold"
              style={{backgroundColor: '#87A96B'}}
            >
              SECOND PHASE
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-800">
            Resources for <span style={{color: '#87A96B'}}>Homeowners</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Guides and tools to help you maintain your home, save money, and protect your investment.
          </p>
        </div>

        {/* Buying & Assistance Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
            <Home className="w-6 h-6 mr-3" style={{color: '#3F5D4A'}} />
            Homebuying & Assistance Programs
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {guidesResources.map((resource, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div 
                      className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{backgroundColor: resource.color + '20'}}
                    >
                      {React.cloneElement(resource.icon, { style: { color: resource.color } })}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-800 text-lg mb-2">{resource.title}</h3>
                      <p className="text-gray-600 text-sm mb-4">{resource.description}</p>
                      <a 
                        href={resource.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 text-white rounded-lg text-sm font-semibold transition-colors"
                        style={{backgroundColor: resource.color}}
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        {resource.action}
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Maintenance Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
            <Wrench className="w-6 h-6 mr-3" style={{color: '#87A96B'}} />
            Home Maintenance & Energy Savings
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {maintenanceResources.map((resource, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div 
                      className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{backgroundColor: resource.color + '20'}}
                    >
                      {React.cloneElement(resource.icon, { style: { color: resource.color } })}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-800 text-lg mb-2">{resource.title}</h3>
                      <p className="text-gray-600 text-sm mb-4">{resource.description}</p>
                      <a 
                        href={resource.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 text-white rounded-lg text-sm font-semibold transition-colors"
                        style={{backgroundColor: resource.color}}
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        {resource.action}
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Support Services Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
            <Shield className="w-6 h-6 mr-3" style={{color: '#3F5D4A'}} />
            Support Services & Counseling
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {supportResources.map((resource, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <div 
                    className="w-12 h-12 rounded-lg flex items-center justify-center mb-4"
                    style={{backgroundColor: resource.color + '20'}}
                  >
                    {React.cloneElement(resource.icon, { style: { color: resource.color } })}
                  </div>
                  <span 
                    className="px-2 py-1 rounded text-xs font-semibold mb-2 inline-block"
                    style={{backgroundColor: resource.color + '20', color: resource.color}}
                  >
                    {resource.type}
                  </span>
                  <h3 className="font-bold text-gray-800 text-lg mb-2">{resource.title}</h3>
                  <p className="text-gray-600 text-sm mb-4">{resource.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {resource.phone && (
                      <a 
                        href={`tel:${resource.phone}`}
                        className="inline-flex items-center px-3 py-2 text-white rounded-lg text-sm font-semibold transition-colors"
                        style={{backgroundColor: resource.color}}
                      >
                        <Phone className="w-4 h-4 mr-2" />
                        {resource.phone}
                      </a>
                    )}
                    <a 
                      href={resource.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-50 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Website
                    </a>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Tips Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Smart Homeowner Tips</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center p-4">
              <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center" style={{backgroundColor: '#87A96B20'}}>
                <ClipboardList className="w-6 h-6" style={{color: '#87A96B'}} />
              </div>
              <h4 className="font-semibold text-gray-800 mb-2">Schedule Regular Maintenance</h4>
              <p className="text-gray-600 text-sm">Small repairs now prevent expensive problems later. Check your home seasonally.</p>
            </div>
            <div className="text-center p-4">
              <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center" style={{backgroundColor: '#3F5D4A20'}}>
                <DollarSign className="w-6 h-6" style={{color: '#3F5D4A'}} />
              </div>
              <h4 className="font-semibold text-gray-800 mb-2">Build an Emergency Fund</h4>
              <p className="text-gray-600 text-sm">Aim to save 1-3% of your home's value annually for repairs and emergencies.</p>
            </div>
            <div className="text-center p-4">
              <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center" style={{backgroundColor: '#87A96B20'}}>
                <FileText className="w-6 h-6" style={{color: '#87A96B'}} />
              </div>
              <h4 className="font-semibold text-gray-800 mb-2">Keep Good Records</h4>
              <p className="text-gray-600 text-sm">Save all receipts, warranties, and records of home improvements for tax and resale benefits.</p>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="bg-[#F0EBE0] border border-[#D4C9B0] rounded-lg p-6 mb-12">
          <h3 className="font-semibold text-[#3F5D4A] mb-2">Important Note</h3>
          <p className="text-[#5C5852] text-sm">
            These resources are provided for informational purposes. Program eligibility and availability may change. 
            Contact each organization directly for the most current information. For personalized advice, 
            consider speaking with a HUD-approved housing counselor.
          </p>
        </div>

        {/* Call to Action */}
        <div className="text-center">
          <div 
            className="rounded-2xl p-8 md:p-12 text-white"
            style={{background: '#3F5D4A'}}
          >
            <h3 className="text-3xl font-bold mb-4">Need Help Getting Started?</h3>
            <p className="text-xl mb-6 opacity-90">
              Whether you're dreaming of homeownership or looking to maintain your current home, we're here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/programs">
                <Button 
                  className="px-8 py-3 rounded-xl font-semibold transition-all duration-200 shadow-lg"
                  style={{
                    background: '#87A96B',
                    borderColor: '#87A96B'
                  }}
                >
                  Explore Our Programs
                </Button>
              </Link>
              <Link to="/resources/financial">
                <Button 
                  variant="outline"
                  className="border-2 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-200"
                  style={{
                    borderColor: '#FEFEFE',
                    color: '#FEFEFE',
                    backgroundColor: 'transparent'
                  }}
                >
                  Financial Resources
                </Button>
              </Link>
            </div>
          </div>
        </div>

        </div>
      </div>
      <Footer />
    </div>
  );
};

export default HomeownersResources;
